/**
 * Live Tapestry rendering systems (TT-1046).
 *
 * The Live Tap page can render a meeting through more than one system:
 *   - classic: the curated text/symbol tapestry (the vendored TapestryCanvas)
 *   - scene:   the server-built "graphic recording" board (SceneCanvas)
 *   - board:   the plan-series graphic-recording board, Live Tapestry v2
 *              (BoardCanvas over scene-board archetype "board")
 *
 * Deliberately a LIST, not a boolean — this is temporary scaffolding while
 * the direction settles; every chooser (modal step, in-page switcher) reads
 * it.
 *
 * The choice is client-side only (no backend schema): persisted in
 * localStorage keyed by doc id, written when a live tap is created or the
 * system is flipped on an open tap. Unknown/missing values fall back to
 * classic so the default experience never changes.
 */

// `panel`: the info panel's tooltip, when the system has one (classic's
// carries the symbol description; the scene's and the board's is the
// transcript alone — TT-1168 note 5, TT-1215); `exportable`: the canvas
// answers exportPng().
export const LIVE_SYSTEMS = [
  {
    id: "classic",
    label: "Classic",
    blurb: "Steady and pre-composed — phrases and symbols fill a curated layout.",
    panel: "Show the symbol description and transcript",
    exportable: true,
  },
  {
    id: "scene",
    label: "Scene (experimental)",
    blurb: "Dynamic and interpretive — an illustrated story that grows with the conversation.",
    panel: "Show the transcript",
    exportable: true,
  },
  {
    id: "board",
    label: "Board (experimental)",
    blurb: "A scribe's board — phrase chunks, watercolor symbols and arrows that never redraw.",
    panel: "Show the transcript",
    exportable: true,
  },
];

/** The system's entry (its chrome flags), or an empty one for an unknown id. */
export const liveSystemInfo = (id) => LIVE_SYSTEMS.find((s) => s.id === id) || {};

export const DEFAULT_LIVE_SYSTEM = "classic";

// Server-side board archetypes -> the live system that renders them. A doc's
// board keeps the archetype it was created with, so a view whose system
// doesn't match must say so instead of polling "held" forever.
export const SYSTEM_FOR_ARCHETYPE = { story: "scene", wall: "scene", board: "board" };

/** The v2 system that owns a doc's board (its archetype's), or null while no board is planned. */
export function boardSystem(board) {
  const archetype = board?.archetype;
  if (!archetype || (board?.upto ?? -1) < 0) return null;
  return SYSTEM_FOR_ARCHETYPE[archetype] || null;
}

export function systemLabel(id) {
  return LIVE_SYSTEMS.find((s) => s.id === id)?.label || id;
}

const storageKey = (docId) => `liveSystem:${docId}`;

function isKnownSystem(id) {
  return LIVE_SYSTEMS.some((s) => s.id === id);
}

// localStorage can throw (private mode, storage disabled); the choice is a
// convenience, so both accessors degrade to the default silently.
export function readLiveSystem(docId) {
  if (!docId) return DEFAULT_LIVE_SYSTEM;
  try {
    const value = window.localStorage.getItem(storageKey(docId));
    return isKnownSystem(value) ? value : DEFAULT_LIVE_SYSTEM;
  } catch {
    return DEFAULT_LIVE_SYSTEM;
  }
}

export function writeLiveSystem(docId, systemId) {
  if (!docId || !isKnownSystem(systemId)) return;
  try {
    window.localStorage.setItem(storageKey(docId), systemId);
  } catch {
    // best effort — an unstorable choice just means the default next visit
  }
}
